import { useEffect, useMemo, useRef, useState } from 'react';
import { api } from '../lib/api.js';
import { useStore } from '../lib/store.js';
import { fmtDate, bytes as fmtBytes } from '../lib/format.js';
import { IconCheck, IconClose, IconUpload } from '../components/Icons.js';

interface UploadDoc {
  upload_id: string;
  file_name: string;
  ext: string;
  size_bytes: number;
  suggested_location: string | null;
  uploaded_at: string;
}

type Location = 'config' | 'plugin' | 'data' | 'lang' | 'harmony';

const LOCATION_OPTIONS: Array<{ value: Location; label: string; hint: string }> = [
  { value: 'config',  label: 'Oxide Config',  hint: 'oxide/config/' },
  { value: 'data',    label: 'Oxide Data',    hint: 'oxide/data/' },
  { value: 'lang',    label: 'Oxide Lang',    hint: 'oxide/lang/en/' },
  { value: 'plugin',  label: 'Oxide Plugins', hint: 'oxide/plugins/' },
  { value: 'harmony', label: 'Harmony Mods',  hint: 'HarmonyMods/' },
];

interface Me { discord_id: string; discord_username: string; role: 'admin' | 'operator' | 'viewer' }

export default function Files({ me }: { me: Me }) {
  const [uploads, setUploads] = useState<UploadDoc[]>([]);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [dispatching, setDispatching] = useState(false);
  const [dispatchResult, setDispatchResult] = useState<null | { total: number; succeeded: number; failed: number; results: Array<{ Server_ID: number; ok: boolean; error?: string }> }>(null);
  const [showDispatchModal, setShowDispatchModal] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const reload = () => api.get<{ uploads: UploadDoc[] }>('/api/files').then((r) => setUploads(r.uploads));
  useEffect(() => { reload(); }, []);

  const canEdit = me.role !== 'viewer';

  const upload = async (files: FileList | File[]) => {
    if (!canEdit) return;
    setUploading(true);
    setUploadError(null);
    try {
      for (const f of files) {
        const fd = new FormData();
        fd.append('file', f, f.name);
        const res = await fetch('/api/files/upload', { method: 'POST', body: fd, credentials: 'include' });
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          throw new Error(body.error ?? `upload failed (${res.status})`);
        }
      }
      await reload();
    } catch (err) {
      setUploadError((err as Error).message);
    } finally {
      setUploading(false);
    }
  };

  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    if (e.dataTransfer.files.length > 0) upload(e.dataTransfer.files);
  };

  const toggleSelect = (id: string) => {
    setSelectedIds((cur) => {
      const next = new Set(cur);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const remove = async (id: string) => {
    if (!confirm('Delete this staged file?')) return;
    await api.delete(`/api/files/${id}`);
    setSelectedIds((cur) => { const n = new Set(cur); n.delete(id); return n; });
    reload();
  };

  const selectedUploads = uploads.filter((u) => selectedIds.has(u.upload_id));

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-2xl font-semibold tracking-tight">Files</h1>
        <p className="text-sm text-fg-muted mt-1 max-w-3xl">
          Upload plugins, configs, lang files, or Harmony mods, then push them to specific servers,
          a group, or an entire machine.
        </p>
      </header>

      {canEdit && (
        <section
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={onDrop}
          className={`card p-8 text-center cursor-pointer transition-colors ${
            dragOver ? '!border-accent !bg-accent/5' : 'hover:bg-bg-hover/30 hover:border-bg-ring'
          }`}
          onClick={() => inputRef.current?.click()}
          role="button"
          tabIndex={0}
        >
          <input
            ref={inputRef}
            type="file"
            multiple
            className="hidden"
            onChange={(e) => { if (e.target.files) upload(e.target.files); e.target.value = ''; }}
          />
          <div className="flex flex-col items-center gap-2">
            <div className={`w-12 h-12 rounded-full flex items-center justify-center transition-colors ${
              dragOver ? 'bg-accent/15 text-accent' : 'bg-bg-raised border border-bg-border text-fg-dim'
            }`}>
              <IconUpload className="w-5 h-5" />
            </div>
            <div className="font-medium text-fg">
              {uploading ? 'Uploading…' : 'Drop files here or click to browse'}
            </div>
            <div className="text-xs text-fg-dim">
              .cs, .dll, .json — auto-detects destination by extension
            </div>
          </div>
          {uploadError && (
            <div className="mt-3 text-sm text-status-error inline-block px-3 py-1.5 rounded-md bg-status-error/10 border border-status-error/30">
              {uploadError}
            </div>
          )}
        </section>
      )}

      <section className="card overflow-hidden">
        {uploads.length === 0 ? (
          <div className="p-10 flex flex-col items-center text-center gap-2">
            <div className="w-12 h-12 rounded-full bg-bg-raised border border-bg-border flex items-center justify-center text-fg-dim">
              <IconUpload className="w-5 h-5" />
            </div>
            <div className="text-sm text-fg-muted">No uploaded files yet</div>
          </div>
        ) : (
          <ul className="divide-y divide-bg-border">
            {uploads.map((u) => {
              const checked = selectedIds.has(u.upload_id);
              return (
                <li key={u.upload_id}>
                  <label className="px-4 py-3 flex items-center gap-3 cursor-pointer hover:bg-bg-hover/40 transition-colors">
                    {canEdit && (
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleSelect(u.upload_id)}
                        className="accent-accent w-4 h-4 cursor-pointer"
                      />
                    )}
                    <FileTypeBadge ext={u.ext} suggested={u.suggested_location} />
                    <div className="flex-1 min-w-0">
                      <div className="font-medium font-mono text-sm truncate text-fg">{u.file_name}</div>
                      <div className="text-xs text-fg-dim">
                        {fmtBytes(u.size_bytes)} · uploaded {fmtDate(u.uploaded_at)}
                      </div>
                    </div>
                    {canEdit && (
                      <button
                        type="button"
                        onClick={(e) => { e.preventDefault(); remove(u.upload_id); }}
                        className="btn-icon hover:!text-status-error"
                        aria-label="Delete file"
                        title="Delete"
                      >
                        <IconClose className="w-4 h-4" />
                      </button>
                    )}
                  </label>
                </li>
              );
            })}
          </ul>
        )}
      </section>

      {canEdit && selectedUploads.length > 0 && (
        <div className="sticky bottom-4 flex justify-end z-10">
          <div className="card px-4 py-3 flex items-center gap-3 shadow-pop animate-slide-up">
            <span className="text-sm text-fg-muted">
              <span className="text-fg font-medium">{selectedUploads.length}</span> file{selectedUploads.length === 1 ? '' : 's'} selected
            </span>
            <button
              className="btn-primary btn-sm"
              onClick={() => setShowDispatchModal(true)}
              disabled={dispatching}
            >
              Push to servers…
            </button>
          </div>
        </div>
      )}

      {showDispatchModal && (
        <DispatchModal
          uploads={selectedUploads}
          onCancel={() => setShowDispatchModal(false)}
          onDispatched={(result) => {
            setShowDispatchModal(false);
            setSelectedIds(new Set());
            setDispatchResult(result);
          }}
          setDispatching={setDispatching}
        />
      )}

      {dispatchResult && (
        <DispatchResultPanel
          result={dispatchResult}
          onDismiss={() => setDispatchResult(null)}
        />
      )}
    </div>
  );
}

function FileTypeBadge({ ext, suggested }: { ext: string; suggested: string | null }) {
  const lower = ext.toLowerCase();
  let label = '?';
  let cls = 'bg-bg-raised text-fg-muted ring-bg-border';
  if (lower === '.cs')        { label = 'CS';   cls = 'bg-accent/15 text-accent ring-accent/30'; }
  else if (lower === '.dll')  { label = 'DLL';  cls = 'bg-status-warn/15 text-status-warn ring-status-warn/30'; }
  else if (lower === '.json') { label = 'JSON'; cls = 'bg-status-online/15 text-status-online ring-status-online/30'; }
  else { label = lower.replace('.', '').toUpperCase() || '—'; }

  return (
    <div className="flex flex-col items-center w-14 shrink-0 gap-0.5">
      <span className={`px-1.5 py-0.5 rounded-md text-[10px] font-bold ring-1 tabular-nums ${cls}`}>{label}</span>
      {suggested && <span className="text-[9px] text-fg-dim mt-0.5 capitalize">{suggested}</span>}
    </div>
  );
}

function DispatchModal({
  uploads, onCancel, onDispatched, setDispatching,
}: {
  uploads: UploadDoc[];
  onCancel: () => void;
  onDispatched: (result: { total: number; succeeded: number; failed: number; results: Array<{ Server_ID: number; ok: boolean; error?: string }> }) => void;
  setDispatching: (b: boolean) => void;
}) {
  const servers = useStore((s) => s.servers);
  const allServers = useMemo(() => Array.from(servers.values()), [servers]);
  const groups = useMemo(() => Array.from(new Set(allServers.map((s) => s.Server_Group))).sort(), [allServers]);
  const machines = useMemo(() => Array.from(new Set(allServers.map((s) => s.Server_Machine))).sort(), [allServers]);

  const defaultLocation = useMemo<Location>(() => {
    const sug = uploads.map((u) => u.suggested_location).filter((x): x is string => !!x);
    if (sug.length === uploads.length && sug.every((s) => s === sug[0])) return sug[0] as Location;
    return 'config';
  }, [uploads]);

  const [location, setLocation] = useState<Location>(defaultLocation);
  const [targetKind, setTargetKind] = useState<'all' | 'group' | 'machine' | 'servers'>('servers');
  const [targetGroup, setTargetGroup] = useState<string>(groups[0] ?? '');
  const [targetMachine, setTargetMachine] = useState<string>(machines[0] ?? '');
  const [selectedServerIds, setSelectedServerIds] = useState<Set<number>>(new Set());
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const target = (() => {
    switch (targetKind) {
      case 'all':     return { kind: 'all' as const };
      case 'group':   return { kind: 'group' as const, group: targetGroup };
      case 'machine': return { kind: 'machine' as const, machine: targetMachine };
      case 'servers': return { kind: 'servers' as const, ids: Array.from(selectedServerIds) };
    }
  })();

  const matched = useMemo(() => {
    switch (targetKind) {
      case 'all':     return allServers;
      case 'group':   return allServers.filter((s) => s.Server_Group === targetGroup);
      case 'machine': return allServers.filter((s) => s.Server_Machine === targetMachine);
      case 'servers': return allServers.filter((s) => selectedServerIds.has(s.Server_ID));
    }
  }, [allServers, targetKind, targetGroup, targetMachine, selectedServerIds]);

  const submit = async () => {
    setBusy(true);
    setDispatching(true);
    setError(null);
    try {
      if (targetKind === 'servers' && selectedServerIds.size === 0) {
        throw new Error('Pick at least one server');
      }
      const res = await api.post<{ total: number; succeeded: number; failed: number; results: Array<{ Server_ID: number; ok: boolean; error?: string }> }>(
        '/api/files/dispatch',
        { upload_ids: uploads.map((u) => u.upload_id), location, target },
      );
      onDispatched(res);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
      setDispatching(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-bg/70 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in" onClick={onCancel}>
      <div className="card p-6 w-full max-w-xl max-h-[90vh] overflow-y-auto shadow-modal animate-slide-up" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
        <h3 className="text-lg font-semibold mb-1">Push files</h3>
        <p className="text-xs text-fg-muted mb-5">
          {uploads.length} file{uploads.length === 1 ? '' : 's'}: {uploads.map((u) => u.file_name).join(', ')}
        </p>

        <div className="space-y-5">
          <div>
            <label className="label">Destination</label>
            <div className="grid grid-cols-1 gap-1.5">
              {LOCATION_OPTIONS.map((opt) => {
                const active = location === opt.value;
                return (
                  <label
                    key={opt.value}
                    className={`flex items-center gap-3 px-3 h-11 rounded-lg border cursor-pointer transition-colors ${
                      active
                        ? 'bg-accent/10 border-accent/60 text-fg'
                        : 'bg-bg-raised border-bg-border text-fg-muted hover:text-fg hover:border-bg-ring'
                    }`}
                  >
                    <input
                      type="radio"
                      name="location"
                      value={opt.value}
                      checked={active}
                      onChange={() => setLocation(opt.value)}
                      className="accent-accent w-4 h-4 cursor-pointer"
                    />
                    <span className="font-medium text-fg">{opt.label}</span>
                    <span className="font-mono text-xs text-fg-dim ml-auto">{opt.hint}</span>
                  </label>
                );
              })}
            </div>
          </div>

          <div>
            <label className="label">Target</label>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              {(['all', 'group', 'machine', 'servers'] as const).map((k) => (
                <button
                  key={k}
                  type="button"
                  onClick={() => setTargetKind(k)}
                  className={`px-3 h-10 text-sm rounded-lg border cursor-pointer transition-colors capitalize ${
                    targetKind === k ? 'bg-accent/10 border-accent/60 text-accent font-medium' : 'bg-bg-raised border-bg-border text-fg-muted hover:text-fg hover:border-bg-ring'
                  }`}
                >
                  {k === 'servers' ? 'Specific' : k}
                </button>
              ))}
            </div>

            {targetKind === 'group' && (
              <select className="input mt-3 w-full" value={targetGroup} onChange={(e) => setTargetGroup(e.target.value)}>
                {groups.map((g) => <option key={g} value={g}>{g}</option>)}
              </select>
            )}
            {targetKind === 'machine' && (
              <select className="input mt-3 w-full" value={targetMachine} onChange={(e) => setTargetMachine(e.target.value)}>
                {machines.map((m) => <option key={m} value={m}>{m}</option>)}
              </select>
            )}
            {targetKind === 'servers' && (
              <div className="mt-3 max-h-48 overflow-y-auto border border-bg-border rounded-lg p-1 bg-bg-raised/30">
                {allServers.map((s) => (
                  <label key={s.Server_ID} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-bg-hover rounded-md px-2 py-1.5">
                    <input
                      type="checkbox"
                      checked={selectedServerIds.has(s.Server_ID)}
                      onChange={() => {
                        setSelectedServerIds((cur) => {
                          const n = new Set(cur);
                          if (n.has(s.Server_ID)) n.delete(s.Server_ID); else n.add(s.Server_ID);
                          return n;
                        });
                      }}
                      className="accent-accent w-4 h-4 cursor-pointer"
                    />
                    <span className="font-medium text-fg">{s.Server_Name}</span>
                    <span className="text-fg-dim text-xs">· {s.Server_Machine} · {s.Server_Group}</span>
                  </label>
                ))}
              </div>
            )}
          </div>

          <div className="rounded-lg bg-bg-raised border border-bg-border px-4 py-3 text-sm leading-relaxed">
            Will write <strong className="text-fg">{uploads.length} file{uploads.length === 1 ? '' : 's'}</strong>
            {' '}to <span className="font-mono text-accent">{LOCATION_OPTIONS.find((l) => l.value === location)!.hint}</span>
            {' '}on <strong className="text-fg">{matched.length} server{matched.length === 1 ? '' : 's'}</strong>.
          </div>

          {error && (
            <div className="text-sm text-status-error px-3 py-2 rounded-md bg-status-error/10 border border-status-error/30">
              {error}
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 mt-6">
          <button className="btn-ghost" onClick={onCancel} disabled={busy}>Cancel</button>
          <button
            className="btn-primary"
            disabled={busy || matched.length === 0}
            onClick={submit}
          >
            {busy ? 'Pushing…' : 'Push'}
          </button>
        </div>
      </div>
    </div>
  );
}

function DispatchResultPanel({
  result, onDismiss,
}: {
  result: { total: number; succeeded: number; failed: number; results: Array<{ Server_ID: number; ok: boolean; error?: string }> };
  onDismiss: () => void;
}) {
  const servers = useStore((s) => s.servers);
  return (
    <section className="card p-5">
      <header className="flex items-center justify-between mb-3">
        <h3 className="font-semibold text-fg">Dispatch result</h3>
        <button className="btn-quiet btn-sm" onClick={onDismiss}>Dismiss</button>
      </header>
      <div className="text-sm text-fg-muted mb-3 flex items-center gap-3 flex-wrap">
        <span className="text-status-online inline-flex items-center gap-1.5">
          <IconCheck className="w-3.5 h-3.5" /> {result.succeeded} succeeded
        </span>
        {result.failed > 0 && (
          <span className="text-status-error">· {result.failed} failed</span>
        )}
        <span className="text-fg-dim">· {result.total} total</span>
      </div>
      {result.failed > 0 && (
        <ul className="text-xs space-y-1">
          {result.results.filter((r) => !r.ok).map((r) => {
            const s = servers.get(r.Server_ID);
            return (
              <li key={r.Server_ID} className="text-fg-muted">
                <span className="text-fg">{s?.Server_Name ?? `#${r.Server_ID}`}</span>:{' '}
                <span className="text-status-error">{r.error}</span>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
